/* eslint-disable react/jsx-no-bind */
import { Button, CircularProgress, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, Typography, Grid } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import ClearIcon from '@material-ui/icons/Clear';
import TableChartOutlinedIcon from '@material-ui/icons/TableChartOutlined';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FiltersContext from '../../contexts/FiltersContext';
import StoryContext from '../../contexts/StoryContext';
import { displayFacilityType, composeWhereClause } from '../../modules/data';
import { rdsTabulate } from '../../modules/rds';
import states, { stateFIPS } from '../../modules/states';
import { toFormattedNumber } from '../../modules/text';

function CountyComparisonTable() {
  const { stateCode } = useParams();
  const {
    primaryColor,
  } = useContext(StoryContext);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [stateTotal, setStateTotal] = useState(0);
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedCounties, setSelectedCounties] = useState([]);

  const { settingType, acceptSubsidies } = useContext(FiltersContext);

  useEffect(() => {
    // const server = new RdsServer(process.env.REACT_APP_RDS_SERVER);
    // Instantiate a catalog that exists on the server
    // const catalog = new RdsCatalog(server, 'ccdc');
    // Instantiate a data product that exists on the catalog
    // const dataProduct = new RdsDataProduct(catalog, 'county_licensed_capacity');
    let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND capacity_type=LIC AND is_near_high_risk_site=1`;

    whereClause += composeWhereClause({ settingType });

    if (acceptSubsidies === 'Accept subsidies') {
      whereClause += ' AND is_subsidized=1';
    } else {
      whereClause += ' AND is_subsidized=0';
    }

    rdsTabulate('ccdc', 'county_licensed_capacity', {
      dims: 'county_fips',
      measure: 'value:SUM(capacity)', // TODO: needs to be sum of capacity
      where: whereClause,
      format: 'amcharts',
      inject: true,
    }, true)
      .then((result) => {
        const d = result.parsedBody.dataProvider;
        const total = d.reduce((a, v) => (a + Number.parseInt(v.value, 10)), 0);
        setStateTotal(total);
        // const payload = d.map((v) => Object.assign(v, { total }));
        // realign data. Update selected counties with fresh data
        const fips = selectedCounties.map((c) => (c.county_fips));
        if (fips.length > 0) {
          setSelectedCounties(_.filter(d, (o) => fips.includes(o.county_fips)));
        }
        setData(d);
      })
      .catch((rdsError) => {
        setError(rdsError);
      })
      .finally(() => {
        setReady(true);
      });
  }, [stateCode, acceptSubsidies, settingType]);

  const handleSelectionChange = (event) => {
    setSelectedCounty(event.target.value);
  };

  const handleAddCounty = () => {
    if (!_.includes(selectedCounties, selectedCounty)) {
      setSelectedCounties(selectedCounties.concat([selectedCounty]));
    }
    setSelectedCounty('');
  };

  const removeCounty = (c) => () => {
    if (!c) return;
    setSelectedCounties(selectedCounties.filter((f) => (f.county_fips !== c.county_fips)));
  };

  if (!ready) {
    return <CircularProgress />;
  }

  return (
    <>
      {error && <Typography variant="body1" color="error">{error.message}</Typography>}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        color: primaryColor,
      }}
      >
        <TableChartOutlinedIcon />&nbsp;<Typography variant="h5">County Comparison Table</Typography>
      </div>
      <Grid container>
        <Grid item xs={8} sm={6} lg={3}>
          <FormControl>
            <InputLabel id="countySelect">County</InputLabel>
            <Select
              labelId="countySelect"
              id="countySelectHelper"
              value={selectedCounty}
              onChange={handleSelectionChange}
            >
              {data.map((d) => (<MenuItem value={d} key={d.county_fips}>{d.county_fips}</MenuItem>))}

            </Select>
            <FormHelperText>Select a county from the menu to add to the comparison table</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={4} sm={6} lg={9}>
          <Button
            disabled={!selectedCounty}
            variant="contained"
            onClick={handleAddCounty}
            style={{
              marginLeft: '20px', marginBottom: '-70px', backgroundColor: primaryColor, color: '#fff',
            }}
            size="small"
          >Add to table
          </Button>
        </Grid>
      </Grid>
      <Table size="small" aria-label="County comparison table" style={{ overflowX: 'auto', display: 'block' }}>
        <TableHead>
          <TableRow>
            <TableCell><Typography variant="body2">County</Typography></TableCell>
            <TableCell align="right">
              <Typography variant="body2">
                Number of slots in <b>{displayFacilityType(settingType).toLowerCase().replace('fcc', 'FCC')}</b> programs in high-risk locations that <b>{acceptSubsidies.toLowerCase()}</b>
              </Typography>
            </TableCell>

            <TableCell>&nbsp;</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow selected>
            <TableCell component="th" scope="row">
              {states[stateCode.toUpperCase()]} (State)
            </TableCell>
            <TableCell align="right">{toFormattedNumber(stateTotal)}</TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
          {selectedCounties.map((row) => (
            <TableRow key={row.county_fips}>
              <TableCell component="th" scope="row">
                {row.county_fips}
              </TableCell>
              <TableCell align="right">{row.value ? toFormattedNumber(row.value) : 'No Data'}</TableCell>
              <TableCell><IconButton size="small" onClick={removeCounty(row)} aria-label={`Remove ${row.county_fips}`}><ClearIcon /></IconButton></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

export default CountyComparisonTable;
